import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { Authorize, Badge, Table } from '@/components';
import { ColumnConfig } from '@/components/table/Table';
import { PaginationConfig } from '@/components/table/TablePagination';
import { BUSINESS_ROLE_GROUPS } from '@/components/security/AppRoles';

import { ServiceVisitType, VisitType, VisitTypeLabels } from '../models';
import { useDeleteServiceVisitType } from '../hooks';

const typeColors: Record<
  VisitType,
  { backgroundColor: string; ballColor: string; textColor: string }
> = {
  [VisitType.PICKUP]: {
    backgroundColor: '#FFF1E3',
    ballColor: '#EC6B01',
    textColor: '#EC6B01',
  },
  [VisitType.DELIVERY]: {
    backgroundColor: '#E3F2FD',
    ballColor: '#1976D2',
    textColor: '#1976D2',
  },
};

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime())
    ? '-'
    : date.toLocaleDateString('es-UY');
};

type Props = {
  items: ServiceVisitType[];
  isLoading: boolean;
  error?: unknown;
  pagination?: PaginationConfig;
};

const ServiceVisitTypesTable = ({
  items,
  isLoading,
  error,
  pagination,
}: Props) => {
  const navigate = useNavigate();
  const { handleDelete } = useDeleteServiceVisitType();

  const columns: ColumnConfig[] = useMemo(
    () => [
      { label: 'Nombre', field: 'name' },
      {
        label: 'Tipo',
        field: 'type',
        render: (value: VisitType) => {
          const colors = typeColors[value] || typeColors[VisitType.DELIVERY];
          return <Badge value={VisitTypeLabels[value] || value} {...colors} />;
        },
      },
      {
        label: 'Operador logístico',
        field: 'logisticOperatorId',
        render: (_value: string | null, rowData: ServiceVisitType) => {
          if (!rowData.logisticOperatorId) return 'Global';
          return (
            rowData.logisticOperator?.name ||
            rowData.logisticOperator?.businessName ||
            rowData.logisticOperatorId
          );
        },
      },
      {
        label: 'Fecha de creación',
        field: 'createdAt',
        render: (value: string) => formatDate(value),
      },
    ],
    []
  );

  const renderActions = (rowData: ServiceVisitType) => (
    <div className='flex gap-1'>
      <IconButton
        onClick={() => navigate(`/business/service-visit-types/${rowData.id}/edit`)}
        size='small'
        title='Editar'
      >
        <EditIcon fontSize='small' />
      </IconButton>
      <Authorize permission={BUSINESS_ROLE_GROUPS.management} fallback={null}>
        <IconButton
          onClick={() => handleDelete(rowData.id, rowData.name)}
          size='small'
          title='Eliminar'
          color='error'
        >
          <DeleteIcon fontSize='small' />
        </IconButton>
      </Authorize>
    </div>
  );

  return (
    <div className='w-full p-5 flex flex-col bg-white rounded-lg shadow-md gap-5'>
      {error ? (
        <div className='text-center text-red-600 py-8'>
          Error al cargar los tipos de visita. Por favor, intente nuevamente.
        </div>
      ) : items.length > 0 ? (
        <Table
          data={items}
          columns={columns}
          renderActions={renderActions}
          isLoading={isLoading}
          pagination={pagination}
        />
      ) : (
        <div className='text-center text-gray-500 py-8'>
          {isLoading
            ? 'Cargando tipos de visita...'
            : 'No hay tipos de visita por el momento'}
        </div>
      )}
    </div>
  );
};

export default ServiceVisitTypesTable;
